/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import * as applicationStatuses from "../rentalContract/applicationStatus";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import {
  fromInsurer,
  fromToBooleanConversionFields,
  fromToDateConversionFields,
  fromToFloatConversionFields,
  fromToIbanConversionFields,
  fromUserInputtedFloat,
  getConvertedFormDataBase,
  hasChangedData,
  persistAdditionalClients,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizeOrganizationEmailData,
  sanitizePersonData,
  toIban,
  toInsurer,
  toUserInputtedFloat
} from "../formServiceFunctions";
import _includes from "lodash/includes";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Customer } from "../../types/Entities/Customer";
import _has from "lodash/has";
import { getProductData } from "../productService";
import _isEmpty from "lodash/isEmpty";
import ApiClient from "../apiClient";
import _merge from "lodash/merge";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { isFlexServiceProduct } from "../advocardService";
import { Person } from "../../types/Entities/Person";
import { gaEvent } from "../googleAnalyticsService";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTY_TYPE]: {
      product: ["contractPartyType", "reasonForDispute"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]: {
      product: [
        "caseCategory",
        "affectedPartyType",
        "contractDate",
        "subjectOfDispute",
        "claimedAmountClient",
        "clientHasAlreadyClaimed",
        "claimedDateClient",
        "claimedDeadlineClient",
        "defenseProcessStatus",
        "paymentOrderDate",
        "enforcementOrderDate",
        "statementOfClaimFiledDate",
        "contractualPartnerHasLawyer",
        "contractWasTakenOverByOpposingParty",
      ],
      opponentLawyer: ["gender", "givenName", "familyName"],
      "opponentLawFirm.organization": ["name", "telephone"],
      "opponentLawFirm.organization.mainEmail": ["email"],
      "opponentLawFirm.organization.address": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      client: ["gender", "givenName", "familyName"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      "client.bankData": ["iban"],
      additionalClients: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]: {
      product: [
        "reasonForDispute",
        "housingDeficienciesDescriptionClient",
        "previousBasicRent",
        "previousRentIncludingHeating",
        "futureBasicRent",
        "futureRentIncludingHeating",
        "depositAmount",
        "cancellationReceivedDate",
        "rentalContractStartDate",
        "rentalContractEndDate",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]: {
      product: ["opposingPartyType", "propertyManagerReference", "hasPropertyManager"],
      "opposingPartyLegalEntity.organization": ["legalName", "telephone"],
      "opposingPartyLegalEntity.organization.mainEmail": ["email"],
      "opposingPartyLegalEntity.organization.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName", "email", "telephone"],
      "opposingPartyPerson.residenceAddress": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      additionalOpposingParties: ["gender", "givenName", "familyName"],
      additionalOpposingPartiesToBeRemoved: ["id"],
      "propertyManager.organization": ["legalName", "telephone", "fax"],
      "propertyManager.organization.mainEmail": ["email"],
      "propertyManager.organization.address": ["streetAddress", "postalCode", "addressLocality"],
      propertyAddress: ["streetAddress", "postalCode", "addressLocality", "addressAddition"],
    },
    [applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]: {
      product: ["debtCollectionAgencyReference"],
      "debtCollectionAgency.organization": ["name", "telephone"],
      "debtCollectionAgency.organization.mainEmail": ["email"],
      "debtCollectionAgency.organization.address": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]: {
      product: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]: {
      product: ["missingDocuments"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]: {
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      product: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "contractDate",
      ...fromToDateConversionFields,
    },
    {
      path: "claimedDateClient",
      ...fromToDateConversionFields,
    },
    {
      path: "claimedDeadlineClient",
      ...fromToDateConversionFields,
    },
    {
      path: "paymentOrderDate",
      ...fromToDateConversionFields,
    },
    {
      path: "enforcementOrderDate",
      ...fromToDateConversionFields,
    },
    {
      path: "statementOfClaimFiledDate",
      ...fromToDateConversionFields,
    },
    {
      path: "legalExpenseInsurance",
      ...fromToBooleanConversionFields,
    },
    {
      path: "contractualPartnerHasLawyer",
      ...fromToBooleanConversionFields,
    },
    {
      path: "clientHasAlreadyClaimed",
      ...fromToBooleanConversionFields,
    },
    {
      path: "contractWasTakenOverByOpposingParty",
      ...fromToBooleanConversionFields,
    },
    {
      path: "hasPropertyManager",
      ...fromToBooleanConversionFields,
    },
    {
      path: "caseInsurance.deductible",
      ...fromToFloatConversionFields,
    },
    {
      path: "previousBasicRent",
      ...fromToFloatConversionFields,
    },
    {
      path: "previousRentIncludingHeating",
      ...fromToFloatConversionFields,
    },
    {
      path: "futureBasicRent",
      ...fromToFloatConversionFields,
    },
    {
      path: "futureRentIncludingHeating",
      ...fromToFloatConversionFields,
    },
    {
      path: "depositAmount",
      ...fromToFloatConversionFields,
    },
    {
      path: "cancellationReceivedDate",
      ...fromToDateConversionFields,
    },
    {
      path: "rentalContractStartDate",
      ...fromToDateConversionFields,
    },
    {
      path: "rentalContractEndDate",
      ...fromToDateConversionFields,
    },
    {
      path: "claimedAmountClient",
      convertFrom: (value: string | null) => fromUserInputtedFloat(value),
      convertTo: (value: string | null) => toUserInputtedFloat(value),
    },
    {
      path: "client.bankData.iban",
      ...fromToIbanConversionFields,
    },
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
  ];

  statusFunctions = {
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: () => {
      gaEvent({
        category: "Power of Attorney",
        action: "Contract PoA granted",
      });
    },
  };

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    createMutation,
    refreshCustomer,
    deleteMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};

    let hasCustomerUpdates = false;

    if (_has(fields, "customer.person.residenceAddress")) {
      hasCustomerUpdates = true;
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: customer.person!.residenceAddress!.id!,
        data: addressData,
      });
    }

    if (_has(fields, "additionalOpposingParties")) {
      await Promise.all(
        formData.additionalOpposingParties.map(async (person: Person) => {
          const personData = {
            givenName: person.givenName,
            familyName: person.familyName,
            gender: person.gender,
            type: "processParticipant",
            labelType: "additionalOpposingParty",
            customerSpecifiedCaseId: product.id,
          };

          if (!person.id) {
            await createMutation.mutateAsync({
              uri: "/people",
              data: personData,
            });
          } else {
            const originalData = (product.additionalOpposingParties || []).find((p: Person) => p.id === person.id);

            if (hasChangedData(personData, originalData, ["customerSpecifiedCaseId"])) {
              await updateMutation.mutateAsync({
                uri: "/people",
                id: person.id,
                data: personData,
              });
            }
          }
        })
      );
    }

    if (_has(fields, "additionalOpposingPartiesToBeRemoved") && formData.additionalOpposingPartiesToBeRemoved?.length > 0) {
      await Promise.all(
        formData.additionalOpposingPartiesToBeRemoved.map(async (id: number) => {
          await deleteMutation.mutateAsync({
            uri: "/people",
            id: id,
          });
        })
      );
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = pickAndConvert(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = _includes(["customer", "unknown"], formData.affectedPartyType)
        ? formData.customer.person.residenceAddress
        : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = convertedFormData.affectedPartyType === "customer" ? convertedFormData.customer.person : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "opponentLawyer")) {
      let opponentLawyerData = pickAndConvert(formData.opponentLawyer, fields["opponentLawyer"]);
      opponentLawyerData = sanitizePersonData(opponentLawyerData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: formData.opponentLawyer.id,
        data: opponentLawyerData,
      });
    }

    if (_has(fields, "opponentLawFirm.organization")) {
      let opponentLawFirmData = pickAndConvert(formData.opponentLawFirm.organization, fields["opponentLawFirm.organization"]);
      opponentLawFirmData = sanitizeOrganizationData(opponentLawFirmData);
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: formData.opponentLawFirm.organization.id,
        data: opponentLawFirmData,
      });
    }

    if (_has(fields, "debtCollectionAgency.organization")) {
      let debtCollectionAgencyData = pickAndConvert(formData.debtCollectionAgency.organization, fields["debtCollectionAgency.organization"]);
      debtCollectionAgencyData = sanitizeOrganizationData(debtCollectionAgencyData);
      if (product.debtCollectionAgency === null) {
        product = await updateMutation.mutateAsync({
          uri: getProductData(product.name, "apiUri"),
          id: product.id,
          data: {
            debtCollectionAgency: "/organizations/new",
          },
        });
      }
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: product.debtCollectionAgency.organization.id,
        data: debtCollectionAgencyData,
      });

      if (_has(fields, "debtCollectionAgency.organization.mainEmail")) {
        let emailData = pickAndConvert(formData.debtCollectionAgency.organization.mainEmail, fields["debtCollectionAgency.organization.mainEmail"]);
        emailData = sanitizeOrganizationEmailData(emailData);
        await updateMutation.mutateAsync({
          uri: "/organization_emails",
          id: product.debtCollectionAgency.organization.mainEmail.id,
          data: emailData,
        });
      }

      if (_has(fields, "debtCollectionAgency.organization.address")) {
        let debtCollectionAgencyAddressData = pickAndConvert(
          formData.debtCollectionAgency.organization.address,
          fields["debtCollectionAgency.organization.address"]
        );
        debtCollectionAgencyAddressData = sanitizeAddressData(debtCollectionAgencyAddressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.debtCollectionAgency.organization.address.id,
          data: debtCollectionAgencyAddressData,
        });
      }
    }

    if (_has(fields, "opponentLawFirm.organization.mainEmail")) {
      let emailData = pickAndConvert(formData.opponentLawFirm.organization.mainEmail, fields["opponentLawFirm.organization.mainEmail"]);
      emailData = sanitizeOrganizationEmailData(emailData);
      await updateMutation.mutateAsync({
        uri: "/organization_emails",
        id: product.opponentLawFirm.organization.mainEmail.id,
        data: emailData,
      });
    }

    if (_has(fields, "opponentLawFirm.organization.address")) {
      let opponentLawFirmAddressData = pickAndConvert(formData.opponentLawFirm.organization.address, fields["opponentLawFirm.organization.address"]);
      opponentLawFirmAddressData = sanitizeAddressData(opponentLawFirmAddressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: formData.opponentLawFirm.organization.address.id,
        data: opponentLawFirmAddressData,
      });
    }

    if (_has(fields, "client.bankData")) {
      const bankData = pickAndConvert(formData.client.bankData, fields["client.bankData"]);
      if (bankData.iban) {
        bankData.iban = toIban(bankData.iban);
      } else {
        delete bankData.iban;
      }
      if (!_isEmpty(bankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.client.bankData.id,
          data: bankData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity.organization")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyLegalEntity.organization, fields["opposingPartyLegalEntity.organization"]);
        opposingPartyData = sanitizeOrganizationData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.opposingPartyLegalEntity.organization.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.mainEmail")) {
        let emailData = pickAndConvert(formData.opposingPartyLegalEntity.organization.mainEmail, fields["opposingPartyLegalEntity.organization.mainEmail"]);
        emailData = sanitizeOrganizationEmailData(emailData);
        await updateMutation.mutateAsync({
          uri: "/organization_emails",
          id: product.opposingPartyLegalEntity.organization.mainEmail.id,
          data: emailData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.address")) {
        let addressData = pickAndConvert(formData.opposingPartyLegalEntity.organization.address, fields["opposingPartyLegalEntity.organization.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyLegalEntity.organization.address.id,
          data: addressData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyPerson, fields.opposingPartyPerson);
        opposingPartyData = sanitizePersonData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.opposingPartyPerson.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = pickAndConvert(formData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyPerson.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (formData.hasPropertyManager === "yes") {
      if (_has(fields, "propertyManager.organization")) {
        let propertyManagerData = pickAndConvert(formData.propertyManager.organization, fields["propertyManager.organization"]);
        propertyManagerData = sanitizeOrganizationData(propertyManagerData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.propertyManager.organization.id,
          data: propertyManagerData,
        });
      }

      if (_has(fields, "propertyManager.organization.mainEmail")) {
        let emailData = pickAndConvert(formData.propertyManager.organization.mainEmail, fields["propertyManager.organization.mainEmail"]);
        emailData = sanitizeOrganizationEmailData(emailData);
        await updateMutation.mutateAsync({
          uri: "/organization_emails",
          id: product.propertyManager.organization.mainEmail.id,
          data: emailData,
        });
      }

      if (_has(fields, "propertyManager.organization.address")) {
        let addressData = pickAndConvert(formData.propertyManager.organization.address, fields["propertyManager.organization.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.propertyManager.organization.address.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "caseInsurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let caseInsuranceData = pickAndConvert(convertedFormData.caseInsurance, fields["caseInsurance"]);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? formData.customer.person : formData.caseInsurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    if (_has(fields, "additionalClients")) {
      await persistAdditionalClients(formData, product, updateMutation, createMutation, deleteMutation);
    }

    if (_has(fields, "propertyAddress")) {
      let addressData = pickAndConvert(formData.propertyAddress, fields.propertyAddress);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.propertyAddress.id,
        data: addressData,
      });
    }

    let productData = {};
    if (nextApplicationStatus) {
      (productData as { applicationStatus: number }).applicationStatus = nextApplicationStatus;
      // @ts-ignore
      this.statusFunctions[applicationStatus] && this.statusFunctions[applicationStatus]();
    }

    if (fields && fields.product) {
      productData = _merge(productData, pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }
    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Record<string, any>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    howManyClients: product.additionalClients!.length > 1 ? "threeOrMoreClients" : product.additionalClients!.length === 1 ? "twoClients" : "oneClient",
    numberOfAdditionalClients: product.additionalClients?.length ? String(product.additionalClients.length) : "",
    contractAddressIsCurrentAddress: "",
    opposingPartyCount: product.additionalOpposingParties.length > 0 ? "multiple" : "singular",
    additionalOpposingPartiesToBeRemoved: [],
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    const formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    if (isFlexServiceProduct(product)) {
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTY_TYPE]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

      return availableSteps;
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTY_TYPE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
    if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]);
    }
    if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]);
    }
    if (formData.caseCategory === "defendClaim" && formData.defenseProcessStatus === "debtCollection") {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
    // invalidate our next step since formdata updates before the actual request is fired
    if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && !product.powerOfAttorney) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
